/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import LicensingFieldsForHookForm from '@/components/forms/predefined/LicensingFieldsForHookForm'
import React, { useState } from 'react'
import {
  historyItems,
  validationSchema
} from './handlers'
import LicenseHistoryCard from '@/components/license-history/LicenseHistoryCard'
import AvailableAssets from './components/AvailableAssets'
import RegularTemplate from '@/components/templates/RegularTemplate'
import {
  BACK_TO_SAM_LISTING,
  MANAGE_SAM_LISTINGS_DESCRIPTION,
  LIST_SAMS_FOR_LICENSING_TITLE
} from '@/configs/strings/manage-sam-listings'
import { manageSamListingsUrls } from '@/configs/urls'
import { Flex } from '@chakra-ui/react'
import CurrentIA from './components/CurrentIA'
import AICompanyModels from '@/components/forms/predefined/AICompanyModels'
import { useForm } from 'react-hook-form'
import { getValuesFromMultipleCheckboxesGrouped } from '@/utils/forms'
import { joiResolver } from '@hookform/resolvers/joi'
import { useResponsive } from '@/hooks/useResponsive'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import { useParams } from 'react-router-dom'
import { getCompanyWithAiUsageLicense } from '@/services/sams'
import ActionButtons from './components/ActionButtons'
import GeographiesCheckboxes from '@/components/forms/predefined/GeographiesCheckboxes'

const ManageSamListing = () => {
  const [isLoading, setIsLoading] = useState(false)
  const { manageId } = useParams()
  const {
    itemsResponse,
    loading
  } = useFetchItemsWithParamsV2({
    fetchService: getCompanyWithAiUsageLicense,
    fetchParams: { manageId }
  })
  const { company, allowedModels } = itemsResponse || {}
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    register
  } = useForm({
    resolver: joiResolver(validationSchema)
  })

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      console.info('data', data)

      await new Promise(resolve => setTimeout(resolve, 1000))
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }
  const handleLicenseGeographyChanges = (ids) => {
    setValue('licenseGeography', ids, {
      shouldValidate: true
    })
  }
  const handleAiAllowedChanges = (groupedIds) => {
    const ids = getValuesFromMultipleCheckboxesGrouped(groupedIds)
    const currentValues = ids.length > 0
      ? ids
      : undefined

    setValue('aiAllowed', currentValues, {
      shouldValidate: true
    })
  }
  const { flexDirection, flexGap } = useResponsive()

  return (
    <RegularTemplate
      backLabel={BACK_TO_SAM_LISTING}
      backUrl={`/${manageSamListingsUrls.base}`}
      loadingScreen={loading || isLoading}
      childrenHeader={
        () => <CurrentIA
          iaName={company?.name}
          urlImage={company?.logoUrl}
        />
      }
      description={MANAGE_SAM_LISTINGS_DESCRIPTION}
      title={LIST_SAMS_FOR_LICENSING_TITLE}
    >
      <Flex
        flexDirection={{
          base: 'column',
          lg: 'row'
        }}
        gap="20px"
        marginBottom="20px"
      >
        <section>
          <form onSubmit={handleSubmit(onSubmit)}>
            <h2>AI Usage Licensing Preferences</h2>
            <LicensingFieldsForHookForm
              errors={errors}
              register={register}
              numberLicensesKey="numberLicenses"
            />
            <Flex
              flexDirection={flexDirection}
              gap={flexGap}
            >
              <GeographiesCheckboxes
                onChange={handleLicenseGeographyChanges}
                title="License Geography"
              />
              <AICompanyModels
                models={allowedModels}
                errorMessage={errors.aiAllowed?.message}
                onChange={handleAiAllowedChanges}
                label="AI Allowed"
              />
            </Flex>
            <ActionButtons />
          </form>
        </section>
        <section>
          <LicenseHistoryCard
            historyItems={historyItems}
            title={`License History: ${company?.name}`}
          />
        </section>
      </Flex>
      <AvailableAssets
        manageId={manageId}
      />
  </RegularTemplate>

  )
}

export default ManageSamListing
